// Talks to the Api service's /api/v1/chat endpoint. Handles both anonymous
// (pre-sign-in) and authenticated calls. Anonymous calls include a stable
// session id from localStorage so conversations persist across page reloads.
import { rewardsExperience } from '../config/rewardsExperience';
import { teamsExperience } from '../config/teamsExperience';
import type { CmspCaseDetails } from '../../../../../../partner/widget/src/contract';
import { collectClientContext } from './clientContext';
import { createOmnichannelCoordinator, disposeOmnichannelCoordinators } from '../../../../../../partner/ocms/src/omnichannelCoordinator.js';
import { withConversationTrace } from '../../../../../../partner/ocms/src/conversationTrace.js';
export { collectClientContext } from './clientContext';
export type { ClientContextPayload } from './clientContext';

const ANON_SESSION_KEY = 'cmsp.anon.session';
// Persisted id of the user's active conversation. Unlike the anonymous session id (which is a stable
// per-browser identity), this is the id of the CURRENT chat so that a page refresh (F5) reuses it and
// the backend can return the existing history to rehydrate the transcript. Cleared when the user starts
// a new chat or signs out.
const CONVERSATION_ID_KEY = 'cmsp.conversation.id';

const liveHeaders = new Map<string, Record<string, string>>();
const liveIdentities = new Map<string, string>();
const liveOwners = new Map<string, () => Promise<string | null>>();
const liveOwnerRevisions = new Map<string, number>();
let liveOwnerEpoch = 0;
const retainedBrowserConversations = new Map<string, string>();
const browserLive = createOmnichannelCoordinator({
  getContext: collectClientContext,
  getIdentity: id => liveIdentities.get(id ?? '') ?? null,
  getHeaders: async conversationId => {
    const headers = { ...liveHeaders.get(conversationId) };
    const owner = liveOwners.get(conversationId);
    if (owner) {
      const token = await owner();
      if (liveOwners.get(conversationId) !== owner) throw new DOMException('Identity changed.', 'AbortError');
      delete headers.Authorization;
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return withConversationTrace(headers, conversationId);
  },
});

export const isBrowserLiveConversation = (conversationId: string) => browserLive.isBound(conversationId);
export const canSubmitBrowserPreChatSurvey = (conversationId: string) => browserLive.canSubmitPreChatSurvey(conversationId);

/** UI-only read acknowledgement. Legacy read behavior is intentionally untouched. */
export async function markBrowserLiveRead(conversationId: string, messageId: string): Promise<void> {
  if (browserLive.isBound(conversationId)) await browserLive.markRead(conversationId, messageId);
}

/** Retain only CMSP IDs, partitioned by surface and owner, never provider credentials. */
export function retainBrowserConversationId(scope: string, conversationId: string): void {
  if (!browserLive.isActive(conversationId)) return;
  retainedBrowserConversations.set(scope, conversationId);
  setPersistedConversationId(conversationId, `sdk.${scope}`);
}

export function clearRetainedBrowserConversationId(scope: string): void {
  retainedBrowserConversations.delete(scope);
  clearPersistedConversationId(`sdk.${scope}`);
}

/**
 * Abandon a browser-SDK live binding locally, without a provider round-trip or a reconnect attempt.
 * Used by a host (e.g. Teams) that has decided a retained live conversation cannot be resumed. We only
 * abort any in-flight subscription here — we deliberately do NOT call dispose()/end(), because for a
 * non-reconnectable session those perform a clean provider close that itself requires reconnect and
 * throws 'restore_cleanup_required'. The abandoned session is left to expire via its server-side TTL.
 */
export function discardBrowserLiveConversation(conversationId: string): void {
  if (!conversationId) return;
  try { browserLive.detach(conversationId); }
  catch (error) { console.warn('Browser live discard failed', error); }
}

export function getRetainedBrowserConversationId(scope: string): string | undefined {
  const id = retainedBrowserConversations.get(scope) ?? getPersistedConversationId(`sdk.${scope}`) ?? undefined;
  if (id && browserLive.isBound(id) && !browserLive.isActive(id)) {
    clearRetainedBrowserConversationId(scope);
    return undefined;
  }
  if (id) retainedBrowserConversations.set(scope, id);
  return id;
}

/** View detachment retains the scoped resolver. Only ownership teardown revokes it. */
export function attachBrowserLiveOwner(conversationId: string, getBearer: () => Promise<string | null>): (ownershipChanged?: boolean) => void {
  liveOwners.set(conversationId, getBearer);
  return (ownershipChanged = false) => {
    if (liveOwners.get(conversationId) !== getBearer) return;
    browserLive.detach(conversationId);
    if (!ownershipChanged) return;
    void browserLive.dispose(conversationId).catch(error => console.warn('Live ownership teardown failed', error));
    liveOwners.delete(conversationId);
    liveOwnerRevisions.set(conversationId, (liveOwnerRevisions.get(conversationId) ?? 0) + 1);
    liveHeaders.delete(conversationId);
    liveIdentities.delete(conversationId);
    for (const [scope, id] of retainedBrowserConversations) {
      if (id === conversationId) clearRetainedBrowserConversationId(scope);
    }
  };
}

export async function closeBrowserLiveOwnership(): Promise<void> {
  liveOwnerEpoch++;
  const closing = disposeOmnichannelCoordinators();
  for (const id of liveOwners.keys()) {
    liveOwnerRevisions.set(id, (liveOwnerRevisions.get(id) ?? 0) + 1);
  }
  liveOwners.clear();
  liveHeaders.clear();
  liveIdentities.clear();
  for (const scope of retainedBrowserConversations.keys()) clearRetainedBrowserConversationId(scope);
  await closing;
}

// ── Client context (feature 6520909 / 6566880) ──────────────────────
// Browser/device signals sent with each turn so the agent can tailor OS-specific
// steps and surface device facts. On Emerald entry points the server enriches
// osVersion with the real NT build; elsewhere (e.g. the portal SPA) we supply the
// best signals the browser exposes so the feature works regardless of entry point.

// ── InitChat ────────────────────────────────────────────────────────

export interface InitChatRequest {
  /**
   * The conversation to resume (a persisted id), or omitted to start a new one. The server issues conversation
   * ids: a new chat has none, and an id no conversation has used is replaced. Everything after init must use
   * the id the response returns.
   */
  conversationId?: string;
  sourceAppName: string;
  callerAppId: string;
  locale: string;
  region: string;
  isAuthenticated: boolean;
  homeAccountId?: string;
  puid?: string;
  email?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  tenantId?: string;
  entryPoint?: string;
  /**
   * Optional explicit agent name that pins this conversation to a specific agent, bypassing the
   * entry-point routing rules on the server. When set, the API uses this exact agent regardless of
   * entry point / auth / region / puid.
   */
  agentName?: string;
  /**
   * Optional free-text summary (e.g. the full conversation transcript, serialized as JSON) seeded onto
   * the human agent's session when action is "escalation". Ignored otherwise.
   */
  summary?: string;
  /**
   * Optional action to perform at init time. "escalation" connects this conversation directly to the
   * configured human support agent instead of returning an AI greeting — the SAME mechanism a BYOA/static
   * caller uses, and what this SPA calls when the AI itself hands off via request_human_handoff (see
   * ChatWidget.tsx's callEscalationInit).
   */
  action?: 'escalation';
  /**
   * Optional Omnichannel reconnect id, read from the `oc.reconnectid` query parameter of an agent-shared
   * reconnect link. Combined with action:"escalation" it rejoins the customer to their EXISTING
   * conversation and agent instead of queueing a new one. Validate it with checkReconnectAvailability
   * first so an expired link can redirect rather than silently starting a fresh chat.
   */
  reconnectId?: string;
}

export interface InitChatHistoryMessage {
  caseDetails?: CmspCaseDetails | null;
  clientOperationId?: string;
  readSequence?: number;
  readOn?: string;
  senderKind?: 'bot';
  /** Browser/provider display history only; no download URLs or credentials. */
  attachments?: LiveAttachment[];
  /** Original provider body, without the text-only attachment caption. */
  bodyText?: string;
  /** Explicit live lifecycle metadata on system rows; absent for legacy/AI history. */
  liveCategory?: string | null;
  /** Opaque, session-scoped live message component ID; absent for legacy history. */
  messageId?: string | null;
  /** "user" | "agent" | "system" — matches the server-side ConversationMessage roles. */
  role: string;
  text: string;
  /** Agent messages only: the server turn id feedback is submitted against. */
  turnId?: string | null;
  /** Agent messages only: whether this turn is eligible for thumbs feedback (KB-grounded answer). */
  feedbackEligible?: boolean;
  /** Agent messages only: the user's previously-submitted rating, "up" | "down" | null. */
  feedback?: 'up' | 'down' | null;
  /** Agent messages only: grounding citations to re-render the sources link. */
  citations?: Citation[] | null;
  /**
   * Agent messages only: true when a HUMAN live agent wrote this turn (post-handoff). Restores the
   * "Live agent" attribution on resume instead of re-rendering the human's words as the assistant.
   */
  isLiveAgent?: boolean;
  /**
   * Live-agent messages and lifecycle notices: the human display name, so a resumed
   * conversation names them instead of falling back to the generic "Live agent" label. Null when
   * Omnichannel reported no name.
   */
  senderName?: string | null;
}

export interface InitChatResponse {
  liveSdkDeniedReason?: string | null;
  /** Explicit capability negotiation; absent/false preserves the legacy server bridge. */
  liveSdkEnabled?: boolean;
  liveCapabilities?: { incomingReadReceipts: boolean; sendReadReceipts: boolean };
  conversationId: string;
  isNew: boolean;
  greeting: string | null;
  authenticated: boolean;
  /** Populated only for a direct human escalation (action:"escalation") — the D365 widget to connect to. */
  handoffConfig?: HandoffConfig | null;
  /**
   * "live" when this conversation is connected to a human agent — either because this init call
   * escalated directly (action:"escalation"), or because a resumed conversation is still in an active
   * Omnichannel handoff (e.g. after a page refresh mid-live-chat).
   */
  mode?: string | null;
  /** Optional description of the agent selected for this source app. */
  agentintroduction?: string | null;
  /** Server-owned bridge session id to resume polling against when mode === "live". */
  sessionId?: string | null;
  /**
   * Prior messages for an existing conversation (resume-on-refresh). Present only when init was called
   * for a conversation id that still has server-side history (isNew === false). Ordered oldest → newest.
   */
  history?: InitChatHistoryMessage[] | null;
}

/**
 * Thrown when /chat/init refuses a conversation id the caller does not own (HTTP 404
 * `conversation_not_found` — deliberately indistinguishable from an id that never existed).
 */
export class ConversationNotFoundError extends Error {
  constructor(public readonly conversationId: string) {
    super('initChat 404: conversation_not_found');
    this.name = 'ConversationNotFoundError';
  }
}

/**
 * Initializes (or upgrades) the chat session identity in the API cache.
 * Call once before opening the chat window, and again after mid-conversation sign-in.
 * Bearer token is always required (S2S auth).
 *
 * If the server refuses the conversation id because it belongs to another account (e.g. a signed-in
 * conversation persisted in this browser, now resumed without a usable token), the stale persisted id is
 * forgotten and init is retried ONCE with a fresh conversation id, so the customer gets a working new chat
 * rather than a dead widget. Opt-in via `recover` (session start only — never for an escalation of the chat on screen);
 * callers that opt in must use the returned conversationId.
 */
export async function initChat(
  request: InitChatRequest,
  authBearer?: string | null,
  recover?: { persistenceScope?: string },
): Promise<InitChatResponse> {
  try {
    return await initChatOnce(request, authBearer);
  } catch (error) {
    if (!recover || !(error instanceof ConversationNotFoundError)) throw error;
    if (getPersistedConversationId(recover.persistenceScope) === request.conversationId) {
      clearPersistedConversationId(recover.persistenceScope);
    }
    return initChatOnce({ ...request, conversationId: undefined }, authBearer);
  }
}

async function initChatOnce(
  request: InitChatRequest,
  authBearer?: string | null,
): Promise<InitChatResponse> {
  const revision = (request.conversationId && liveOwnerRevisions.get(request.conversationId)) || 0;
  const epoch = liveOwnerEpoch;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (authBearer) {
    headers['Authorization'] = `Bearer ${authBearer}`;
  }

  const res = await fetch('/api/v1/chat/init', {
    method: 'POST',
    headers: withConversationTrace(headers, request.conversationId),
    body: JSON.stringify({ ...request, liveSdkSupported: true }),
  });

  if (!res.ok) {
    const body = await res.text();
    if (res.status === 404 && body.includes('conversation_not_found')) {
      throw new ConversationNotFoundError(request.conversationId ?? '');
    }
    throw new Error(`initChat ${res.status}: ${body}`);
  }

  const response = (await res.json()) as InitChatResponse;
  if (epoch !== liveOwnerEpoch
    || (request.conversationId && (liveOwnerRevisions.get(request.conversationId) ?? 0) !== revision)) {
    throw new DOMException('Conversation detached.', 'AbortError');
  }
  // The server issues the conversation id (AB#6752065). Everything after init is keyed by the id it returned.
  const conversationId = response.conversationId;
  if (!conversationId) throw new Error('initChat: the server returned no conversation id');
  liveHeaders.set(conversationId, {
    ...headers,
    'X-CMSP-Conversation-Id': conversationId,
    'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
    'X-CMSP-Entry-Point': request.entryPoint ?? detectEntryPoint(),
    'Accept-Language': request.locale,
  });
  liveIdentities.set(conversationId, request.homeAccountId
    ? `user:${request.homeAccountId}` : `anonymous:${getAnonSessionId()}`);
  return browserLive.initialize(conversationId, response);
}

// ── Omnichannel reconnect link ───────────────────────────────────────

/** Query parameter Omnichannel's reconnect link uses (see the OC Chat SDK's reconnect sample). */
export const RECONNECT_ID_PARAM = 'oc.reconnectid';

/** Result of validating a reconnect id against Omnichannel. */
export interface ReconnectAvailability {
  /** True when the conversation is still resumable — rejoin it with initChat + action:"escalation". */
  available: boolean;
  /**
   * Where to send the customer when the conversation is no longer resumable. Configured per widget in
   * D365; null means "no redirect configured", i.e. just start a fresh chat.
   */
  redirectUrl?: string | null;
}

/**
 * Reads the reconnect id from the current URL, if the customer arrived on an agent-shared reconnect link.
 * Returns null when absent or not a plausible OC id — the server rejects malformed ids anyway, but there
 * is no reason to make the round trip for junk.
 *
 * Checks the fragment as well as the query string. Omnichannel builds the link by concatenation
 * (`<Portal URL>?oc.reconnectid=<id>`), and this SPA is hash-routed (`#teams`, `#/admin`), so a Portal URL
 * configured with a fragment produces `#teams?oc.reconnectid=x` — where `location.search` is empty and
 * reading it alone would no-op silently, which is the worst failure mode for this feature.
 */
export function readReconnectIdFromUrl(): string | null {
  const pick = (queryString: string): string | null => {
    const raw = new URLSearchParams(queryString).get(RECONNECT_ID_PARAM);
    if (!raw) return null;
    return /^[A-Za-z0-9_-]{1,128}$/.test(raw) ? raw : null;
  };
  try {
    const fromSearch = pick(window.location.search);
    if (fromSearch) return fromSearch;

    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    return queryStart >= 0 ? pick(hash.slice(queryStart + 1)) : null;
  } catch {
    return null;
  }
}

/**
 * Removes the reconnect id from the address bar without navigating, so a refresh (or a shared/bookmarked
 * URL) doesn't attempt to redeem the same link again — by then the conversation is bound to this
 * browser's own conversation id and re-redeeming would start a second session. Strips from the fragment
 * too, for the hash-routed case described above.
 */
export function stripReconnectIdFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    let changed = false;

    if (url.searchParams.has(RECONNECT_ID_PARAM)) {
      url.searchParams.delete(RECONNECT_ID_PARAM);
      changed = true;
    }

    const queryStart = url.hash.indexOf('?');
    if (queryStart >= 0) {
      const hashParams = new URLSearchParams(url.hash.slice(queryStart + 1));
      if (hashParams.has(RECONNECT_ID_PARAM)) {
        hashParams.delete(RECONNECT_ID_PARAM);
        const rest = hashParams.toString();
        url.hash = url.hash.slice(0, queryStart) + (rest ? `?${rest}` : '');
        changed = true;
      }
    }

    if (changed) {
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* URL parsing failed; leave the address bar untouched. */
  }
}

/**
 * Result of a live-session slash command run over the chat stream.
 *
 * `ok` is the server's machine-readable verdict (`liveCommandOk` on the terminal `done` frame), never a
 * reading of the reply text: that text is display copy and may be reworded or localized.
 */
export interface LiveCommandResult {
  ok: boolean;
  redirectUrl?: string | null;
  /** The server-authored reply, for surfaces that want to show it. */
  text?: string | null;
}

/**
 * Runs one live-session slash command (`/reconnect`, `/endchat`, `/chatstatus`, `/readmarkstatus`) over
 * POST /api/v1/chat/stream, the single chat entry point that replaced the per-operation live REST routes.
 *
 * The server intercepts these turns before the model and answers on the terminal `done` frame, so this
 * reads the SSE body just far enough to find that frame instead of going through sendChatMessageStream:
 * no tokens are ever emitted, and these turns must not touch the widget's chat state.
 *
 * `keepalive` is for teardown callers (`/endchat` on unload). The request then outlives the page, so the
 * response is deliberately not read — the operation is best effort and the session TTL is the backstop.
 */
async function runLiveCommand(args: {
  command: string;
  conversationId: string;
  authBearer?: string | null;
  entryPoint?: string;
  locale?: string;
  keepalive?: boolean;
}): Promise<LiveCommandResult> {
  if (browserLive.isBound(args.conversationId)) {
    if (args.command === '/endchat') await browserLive.end(args.conversationId);
    else if (args.command === '/chatstatus') await browserLive.typing(args.conversationId);
    else throw new Error('This command is unavailable for browser live chat.');
    return { ok: true };
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-CMSP-Conversation-Id': args.conversationId,
    'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
    'X-CMSP-Entry-Point': args.entryPoint ?? detectEntryPoint(),
    'Accept-Language': args.locale || navigator.language || 'en-US',
  };
  if (args.authBearer) {
    headers['Authorization'] = 'Bearer ' + args.authBearer;
  }

  const res = await fetch('/api/v1/chat/stream', {
    method: 'POST',
    headers: withConversationTrace(headers, args.conversationId),
    // No clientContext: like a poll turn this never reaches the model, so collecting it would be paid on
    // every keystroke for something nothing reads.
    body: JSON.stringify({ message: args.command, conversationId: args.conversationId }),
    ...(args.keepalive ? { keepalive: true } : {}),
  });

  if (!res.ok) {
    throw new Error(`${args.command.split(' ')[0]} ${res.status}`);
  }
  if (args.keepalive || !res.body) {
    return { ok: true };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: LiveCommandResult = { ok: false };

  const readFrame = (frame: string) => {
    const data = frame
      .split('\n')
      .map(line => (line.endsWith('\r') ? line.slice(0, -1) : line))
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;
    try {
      const event = JSON.parse(data) as StreamEvent;
      if (event.type === 'done') {
        result = {
          ok: event.liveCommandOk === true,
          redirectUrl: event.redirectUrl ?? null,
          text: event.text ?? null,
        };
      }
    } catch {
      /* Not a JSON frame (comment / keep-alive) — ignore. */
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() ?? '';
    frames.forEach(readFrame);
  }
  if (buffer.trim()) readFrame(buffer);

  return result;
}

/**
 * Validates a reconnect id before the widget commits to rejoining the conversation.
 *
 * The stream is a per-conversation endpoint, so an id is always sent; the caller has none yet at this point
 * (chat/init issues it only after the check passes), and a throwaway id is safe because the server answers this
 * one command before any conversation exists, without an init record.
 */
export async function checkReconnectAvailability(
  request: { reconnectId: string; entryPoint?: string; locale?: string; region?: string; conversationId?: string },
  authBearer?: string | null,
): Promise<ReconnectAvailability> {
  const args = [`id=${request.reconnectId}`];
  if (request.entryPoint) args.push(`entrypoint=${request.entryPoint}`);
  if (request.locale) args.push(`locale=${request.locale}`);
  if (request.region) args.push(`region=${request.region}`);

  const result = await runLiveCommand({
    command: `/reconnect ${args.join(' ')}`,
    conversationId: request.conversationId ?? crypto.randomUUID(),
    authBearer,
    entryPoint: request.entryPoint,
    locale: request.locale,
  });

  return { available: result.ok, redirectUrl: result.redirectUrl ?? null };
}

// ── Transcript ───────────────────────────────────────────────────────

/** One turn of the server-assembled transcript (mirrors ConversationTranscriptEntry). */
export interface ChatTranscriptEntry {
  role: 'user' | 'agent' | 'system';
  text: string;
  isLiveAgent?: boolean;
  at?: string;
  citations?: Citation[] | null;
}

/** A conversation transcript assembled server-side (mirrors ConversationTranscript). */
export interface ChatTranscript {
  conversationId?: string;
  entries: ChatTranscriptEntry[];
  /** Which store served it — `'hot'` or `'transcript'`. Informational. */
  source?: string;
  /** True when the OLDEST turns may be missing. */
  truncated?: boolean;
  /** False when the serving store cannot carry citations. */
  citationsAvailable?: boolean;
}

/**
 * Gets the download transcript from browser/provider history for SDK sessions, otherwise the legacy API.
 *
 * The API assembles it from CMSP storage, so it covers turns this page view never rendered AND the
 * human live-agent leg of a handed-off conversation — neither of which the browser holds. Resolves
 * null when there is no server-side record (404 covers both "no such conversation" and "not yours",
 * which the API deliberately does not distinguish), and the caller saves what it rendered instead.
 */
export async function fetchTranscript(
  conversationId: string,
  authBearer?: string | null,
  signal?: AbortSignal,
): Promise<ChatTranscript | null> {
  if (browserLive.isBound(conversationId)) {
    if (signal?.aborted) throw new DOMException('Aborted.', 'AbortError');
    const entries = await browserLive.transcript(conversationId);
    if (signal?.aborted) throw new DOMException('Aborted.', 'AbortError');
    return { conversationId, entries };
  }
  const headers: Record<string, string> = { 'X-CMSP-Conversation-Id': conversationId };
  if (authBearer) {
    headers['Authorization'] = `Bearer ${authBearer}`;
  }

  const res = await fetch(`/api/v1/chat/${encodeURIComponent(conversationId)}/transcript`, {
    method: 'GET',
    headers: withConversationTrace(headers, conversationId),
    signal,
  });

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`fetchTranscript ${res.status}`);
  }

  const body = (await res.json()) as ChatTranscript | null;
  return body && Array.isArray(body.entries) ? body : null;
}

// ── Feedback type ────────────────────────────────────────────────────

export type FeedbackRating = 'up' | 'down';

// ── Chat types ──────────────────────────────────────────────────────

export interface ChatTurnResult {
  conversationId: string;
  turnId: string;
  reply: string;
  endState?: string | null;
  channelAction?: string | null;
  requiresAuth: boolean;
  authenticated: boolean;
  trace?: string | null;
  handoffConfig?: HandoffConfig | null;
  handoffSummary?: string | null;
}

export interface HandoffConfig {
  orgId: string;
  orgUrl: string;
  widgetId: string;
  /** Optional JS callback name the OC widget expects on chatSDKConfig. Defaults to 'getAuthToken'. */
  authTokenMethodName?: string | null;
}

let memoryAnonSessionId: string | undefined;
let memoryOnlyAnonSession = false;
export function getAnonSessionId(): string {
  try {
    const stored = localStorage.getItem(ANON_SESSION_KEY);
    if (stored) {
      memoryAnonSessionId = stored;
      memoryOnlyAnonSession = false;
      return stored;
    }
    if (!memoryOnlyAnonSession) memoryAnonSessionId = crypto.randomUUID();
    memoryAnonSessionId ??= crypto.randomUUID();
    localStorage.setItem(ANON_SESSION_KEY, memoryAnonSessionId);
    memoryOnlyAnonSession = false;
  } catch {
    // Storage can be blocked or full; the owner must still be stable for this page's lifetime.
    memoryAnonSessionId ??= crypto.randomUUID();
    memoryOnlyAnonSession = true;
  }
  return memoryAnonSessionId;
}

/**
 * Clears the persisted anonymous chat-session id. Call on sign-out (or any auth transition)
 * so the next anonymous conversation starts a brand-new server-side session instead of
 * resuming the one bound to the previous user.
 */
export function clearAnonSession(): void {
  memoryAnonSessionId = undefined;
  memoryOnlyAnonSession = false;
  try {
    localStorage.removeItem(ANON_SESSION_KEY);
  } catch {
    /* localStorage may be unavailable (private mode / embedded); ignore. */
  }
}

/**
 * Mirrors the server's `ConversationIdPolicy.IsValidForIngress`: 1-64 chars of `[A-Za-z0-9._-]`.
 * The API rejects anything else with 400 `invalid_conversation_id`, so a stored value that fails this
 * must never be sent — otherwise the widget wedges itself in a permanent, unrecoverable 400 loop.
 */
const MAX_CONVERSATION_ID_LENGTH = 64;
const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9._-]+$/;

export function isValidConversationId(conversationId: string | null | undefined): boolean {
  return (
    !!conversationId &&
    conversationId.length <= MAX_CONVERSATION_ID_LENGTH &&
    CONVERSATION_ID_PATTERN.test(conversationId)
  );
}

/** Storage key for a surface's conversation id. Unscoped = the portal ChatWidget's historical key. */
function conversationIdKey(scope?: string): string {
  return scope ? `${CONVERSATION_ID_KEY}.${scope}` : CONVERSATION_ID_KEY;
}

/**
 * Reads the persisted active conversation id, or null when none is stored or storage is unavailable.
 * A stored value the API would reject (corrupt storage, or an id written by an older build under a
 * looser contract) is discarded here rather than replayed into every request.
 *
 * `scope` partitions storage per chat surface. The default (unscoped) key belongs to the portal
 * ChatWidget; a dedicated surface such as the Rewards page MUST pass its own scope, because the two
 * render in the same origin and a same-document route change would otherwise let one surface resume
 * — and rehydrate the transcript of — a conversation started by the other, under the wrong
 * entry point and agent.
 */
export function getPersistedConversationId(scope?: string): string | null {
  try {
    const stored = localStorage.getItem(conversationIdKey(scope));
    if (stored !== null && !isValidConversationId(stored)) {
      clearPersistedConversationId(scope);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

/** Persists the active conversation id so a page refresh resumes the same conversation. */
export function setPersistedConversationId(conversationId: string, scope?: string): void {
  if (!isValidConversationId(conversationId)) {
    // Never persist an id the API would reject on the next request.
    clearPersistedConversationId(scope);
    return;
  }
  try {
    localStorage.setItem(conversationIdKey(scope), conversationId);
  } catch {
    /* localStorage may be unavailable (private mode / embedded); ignore. */
  }
}

/** Clears the persisted conversation id so the next chat starts a brand-new server-side conversation. */
export function clearPersistedConversationId(scope?: string): void {
  try {
    localStorage.removeItem(conversationIdKey(scope));
  } catch {
    /* localStorage may be unavailable (private mode / embedded); ignore. */
  }
}

// Tab-scoped open/closed state for the chat window. sessionStorage (not localStorage) so a refresh in the
// same tab restores the window, but a brand-new tab doesn't auto-open. Lets a mid-conversation refresh
// reopen the widget (and reload its history) instead of collapsing back to the launcher icon.
const CHAT_OPEN_KEY = 'cmsp.chat.open';

/** Whether the chat window was open in this tab before a refresh. */
export function getPersistedChatOpen(): boolean {
  try {
    return sessionStorage.getItem(CHAT_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persists the chat window's open/closed state so a refresh restores it. */
export function setPersistedChatOpen(isOpen: boolean): void {
  try {
    if (isOpen) {
      sessionStorage.setItem(CHAT_OPEN_KEY, '1');
    } else {
      sessionStorage.removeItem(CHAT_OPEN_KEY);
    }
  } catch {
    /* sessionStorage may be unavailable (private mode / embedded); ignore. */
  }
}

/** Detect the entry point: Teams iframe, URL param, or default 'portal'. */
export function detectEntryPoint(): string {
  // Check URL param first (e.g. ?entry=xbox)
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('entry');
  if (fromUrl) return fromUrl;

  // Teams entry point: the legacy #/teams hash OR the en-us clean path (/en-us/teams), so the Teams page
  // resolves the correct agent whether the locale-routing flag is off (hash) or on (clean path).
  if (window.location.hash === teamsExperience.routeHash
      || window.location.pathname.replace(/\/$/, '').toLowerCase() === '/en-us/teams') return teamsExperience.entryPoint;

  if (window.location.hash === rewardsExperience.routeHash) return rewardsExperience.entryPoint;

  // Detect Teams iframe
  try {
    if (window.parent !== window && window.name?.includes('teams')) return 'teams';
  } catch {
    // Cross-origin iframe — assume teams
    return 'teams';
  }

  return 'portal';
}

export async function sendChatMessage(args: {
  message: string;
  conversationId?: string;
  authBearer?: string | null;
  homeAccountId?: string;
  username?: string;
  displayName?: string;
  /** When true, identity was cached via initChat — skip identity headers. */
  useInitChat?: boolean;
  /** Override the entry point (e.g. from the agent selector). Falls back to detectEntryPoint(). */
  entryPoint?: string;
  /** Pin a specific agent by name, bypassing server routing rules (e.g. from the agent selector). */
  agentName?: string;
}): Promise<ChatTurnResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
    'X-CMSP-Entry-Point': args.entryPoint ?? detectEntryPoint(),
    'Accept-Language': navigator.language || 'en-US',
  };

  if (args.agentName) {
    headers['X-CMSP-Agent-Name'] = args.agentName;
  }

  if (args.authBearer) {
    headers['Authorization'] = `Bearer ${args.authBearer}`;
  }

  if (args.conversationId) {
    headers['X-CMSP-Conversation-Id'] = args.conversationId;
  }

  // Only send identity headers when NOT using initChat (legacy fallback).
  if (!args.useInitChat && args.homeAccountId) {
    headers['X-CMSP-User-HomeAccountId'] = args.homeAccountId;
    if (args.username) headers['X-CMSP-User-Username'] = args.username;
    if (args.displayName) headers['X-CMSP-User-DisplayName'] = args.displayName;
  }

  const res = await fetch('/api/v1/chat', {
    method: 'POST',
    headers: withConversationTrace(headers, args.conversationId),
    body: JSON.stringify({
      message: args.message,
      conversationId: args.conversationId,
      clientContext: await collectClientContext(args.conversationId ?? getAnonSessionId()),
    }),
  });

  if (!res.ok) {
    throw new Error(`chat ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as ChatTurnResult;
}

export async function pingHealth(): Promise<boolean> {
  try {
    const res = await fetch('/health', { method: 'GET' });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Streaming (SSE) chat ────────────────────────────────────────────

/** A grounding source the agent cited (web search result or knowledge-base document). */
export interface Citation {
  title: string;
  url: string;
  snippet?: string | null;
}

export interface EscalationDiagnostics {
  stage: string;
  theme?: string | null;
  themeL1?: string | null;
  themeL2?: string | null;
  themeL3?: string | null;
  selectedModality?: string | null;
  eligibilitySource?: string | null;
  modalitySource?: string | null;
  routingSource?: string | null;
  lastErrorCode?: string | null;
  lastFailureStage?: string | null;
  lastFailureDetail?: string | null;
  routingInfoLatencyMs?: number | null;
  routingToHumanChatLatencyMs?: number | null;
  eligibilityDecision?: string | null;
  isWindowsSupported?: boolean | null;
  isWarrantyActive?: boolean | null;
  warrantyEndDatePresent: boolean;
}

export interface EscalationModalityOption {
  id: string;
  displayName: string;
  type: string;
  description?: string | null;
  redirectUrl?: string | null;
  isAvailable: boolean;
  unavailableReason?: string | null;
  operatingHoursText?: string | null;
}

export interface EscalationEligibilityDeviceOption {
  deviceId: string;
  displayName: string;
  osName?: string | null;
  oemModelName?: string | null;
  deviceType?: string | null;
}

export interface EscalationClassification {
  theme: string;
  themeL1?: string | null;
  themeL2?: string | null;
  themeL3?: string | null;
}

export interface EscalationClassificationOption extends EscalationClassification {}

export interface EscalationClassificationPrompt {
  suggested: EscalationClassification;
  options: EscalationClassificationOption[];
}

/**
 * A structured request for user input, streamed as part of an ordinary chat turn.
 *
 * Answered by posting a `promptResponse` turn back to POST /api/v1/chat/stream — the same endpoint
 * that carries every other turn. The client never learns a feature-specific URL, and deliberately
 * never accepts one from the server: the prompt carries a `verb` that only the server can resolve,
 * so a card authored by an external system cannot redirect this client's authenticated POST.
 */
export interface ChatPrompt {
  kind: 'choice' | 'form' | 'adaptive-card';
  verb: string;
  /** Single-use, server-issued. Echo back verbatim; a replayed token is rejected server-side. */
  stepToken: string;
  messageKey?: string | null;
  text?: string | null;
  options?: ChatPromptOption[] | null;
  fields?: ChatPromptField[] | null;
  card?: unknown | null;
}

export interface ChatPromptOption {
  id: string;
  label: string;
  description?: string | null;
  redirectUrl?: string | null;
  isAvailable?: boolean;
  unavailableReason?: string | null;
  detail?: string | null;
  group?: string | null;
}

export interface ChatPromptField {
  id: string;
  label: string;
  type: 'text' | 'tel' | 'choice';
  placeholder?: string | null;
  isRequired?: boolean;
  defaultValue?: string | null;
  options?: ChatPromptOption[] | null;
  maxLength?: number | null;
  /** Why the server rejected the last value for this field; present only on a re-issued form. */
  errorMessage?: string | null;
  errorMessageKey?: string | null;
}

export interface ChatPromptResponse {
  verb: string;
  stepToken: string;
  values: Record<string, string | boolean>;
}

export class AttachmentUnavailableError extends Error {
  constructor(readonly reason: string | null) {
    super(reason ?? 'unavailable');
    this.name = 'AttachmentUnavailableError';
  }
}

/** A file the live agent attached. `fileId` is opaque and only meaningful to the download route. */
export interface LiveAttachment {
  fileId: string;
  fileName?: string | null;
  contentType?: string | null;
}

export interface StreamEvent {
  /** Final handoff decision; null/absent retains init negotiation, true never grants new admission. */
  liveSdkEnabled?: boolean | null;
  liveSdkDisabledReason?: string | null;
  /** Browser transport send-operation correlation; not a local message ordinal. */
  clientOperationId?: string;
  readOn?: string;
  liveCapabilities?: { incomingReadReceipts: boolean; sendReadReceipts: boolean };
  recoveryState?: 'recovering' | 'active' | 'auth-failed' | 'failed';
  /** Presentation-only edit keyed by the original stable messageId. */
  messageUpdated?: boolean;
  /** Stable localization key and opaque arguments for a complete server-authored token message. */
  messageKey?: string | null;
  messageArgs?: readonly string[] | null;
  caseDetails?: CmspCaseDetails | null;
  /** Opaque, session-scoped live message component ID, shared across send/poll replays. */
  messageId?: string | null;
  type: 'start' | 'token' | 'progress' | 'status' | 'done' | 'error';
  text?: string;
  conversationId?: string;
  turnId?: string;
  endState?: string;
  channelAction?: string;
  trace?: string;
  error?: string;
  reply?: string;
  handoffConfig?: HandoffConfig | null;
  handoffSummary?: string | null;
  /** Server-side handoff: "live" when the server handed off to a human agent this turn. */
  mode?: string | null;
  /** Server-owned bridge session id to subscribe to when mode === "live". */
  sessionId?: string | null;
  /** Live-agent frame category when mode === "live": connecting | waiting | queue-position | agent-joined | agent-left | system. */
  liveCategory?: string | null;
  /** Origin of a live-mode token: "human" (the agent's own message) or "system" (a lifecycle frame). */
  senderKind?: string | null;
  /** Display name of the human live agent when Omnichannel provides one. */
  senderName?: string | null;
  /** Server-owned sequence for live-chat send acknowledgements and read receipts. */
  readSequence?: number | null;
  /** Files attached by the live agent on this live-mode token. */
  attachments?: LiveAttachment[] | null;
  /**
   * Pre-chat survey Adaptive Card, present when mode === "prechat-survey" (Teams handoff + survey flag).
   * The widget renders it, collects answers, and posts them to startLiveAgentSession to go live.
   */
  preChatSurvey?: unknown;
  citations?: Citation[];
  /** Post-chat (Customer Voice) survey URL on the terminal done of a live session that opted in. */
  postChatSurveyUrl?: string | null;
  /**
   * True on a live-mode done when the customer's message could NOT be delivered (the OC session was still
   * warming up on another replica). The widget should re-send the same message once.
   */
  retryMessage?: boolean;
  /** True when this turn presented a KB-grounded answer/solution (thumbs feedback should be shown). */
  feedbackEligible?: boolean;
  /** Non-PII Peridot state surfaced into hidden HTML attributes for business validation. */
  escalationDiagnostics?: EscalationDiagnostics | null;
  /**
   * Structured request for user input. Supersedes `availableModalities`, `eligibilityDevices`,
   * `classificationPrompt` and `preChatSurvey` — prefer this; the others remain only until every
   * client has migrated.
   */
  prompt?: ChatPrompt | null;
  /**
   * Redirect link for a chosen non-agent modality (Xbox help, Sign-in Helper, Product Activation).
   * Distinct from `trace`, which carries diagnostic output.
   */
  redirectUrl?: string | null;
  availableModalities?: EscalationModalityOption[] | null;
  eligibilityDevices?: EscalationEligibilityDeviceOption[] | null;
  classificationPrompt?: EscalationClassificationPrompt | null;
  /**
   * `progress` events only: the step kind — `routing` | `skill` | `tool` | `answering`. The server owns
   * this vocabulary; `text` carries the agent-authored wording shown to the customer. Only skill-based
   * agents that authored progress wording emit these, so an agent without them simply streams as before.
   */
  progressPhase?: string | null;
  /** `progress` events only: 1-based order of the step within the turn. */
  progressSequence?: number;
  /**
   * `done` events only: the live-session slash command this turn executed (`reconnect` | `endchat` |
   * `chatstatus` | `readmarkstatus`). Absent on ordinary chat turns.
   */
  liveCommand?: string | null;
  /**
   * `done` events only: whether that command succeeded. Machine-readable on purpose — `text` is
   * display copy and may be reworded or localized, so never infer the outcome from it.
   */
  liveCommandOk?: boolean;
}

/** One narrated step of what the agent is doing, as rendered by the widget. */
export interface ProgressStep {
  phase?: string | null;
  text: string;
  sequence: number;
}

/**
 * Ends the customer's live-agent session. Fire-and-forget by design: called from teardown paths (widget
 * close, page unload), so it uses `keepalive` and never waits on a reply — the session TTL is the backstop.
 */
export async function endLiveSession(
  conversationId: string,
  authBearer?: string | null,
): Promise<void> {
  if (!isValidConversationId(conversationId)) return;
  if (browserLive.isBound(conversationId)) {
    await browserLive.end(conversationId);
    return;
  }

  try {
    await runLiveCommand({
      command: '/endchat',
      conversationId,
      authBearer,
      keepalive: true,
    });
  } catch {
    // Best effort by design — the session's own TTL is the backstop.
  }
}

const TYPING_MIN_INTERVAL_MS = 3000;
const lastTypingSentAt = new Map<string, number>();

/**
 * Relays "the customer is typing" to the human agent's console. Cosmetic, so failures are swallowed and
 * the client keeps its own ~3s throttle on top of the server-side per-caller gate.
 */
export async function sendLiveTyping(
  conversationId: string,
  authBearer?: string | null,
): Promise<void> {
  if (!isValidConversationId(conversationId)) return;

  const now = Date.now();
  const last = lastTypingSentAt.get(conversationId) ?? 0;
  if (now - last < TYPING_MIN_INTERVAL_MS) return;
  lastTypingSentAt.set(conversationId, now);

  try {
    await runLiveCommand({ command: '/chatstatus', conversationId, authBearer });
  } catch {
    // Cosmetic by design — the next keystroke sends another.
  }
}

export function resetLiveTyping(conversationId?: string): void {
  if (conversationId) {
    lastTypingSentAt.delete(conversationId);
  } else {
    lastTypingSentAt.clear();
  }
}

export async function downloadLiveAttachment(args: {
  conversationId: string;
  fileId: string;
  contentType?: string | null;
  authBearer?: string;
  homeAccountId?: string;
  username?: string;
}): Promise<{ blob: Blob; fileName: string }> {
  if (browserLive.isBound(args.conversationId)) {
    return browserLive.download(args.conversationId, args.fileId);
  }
  const headers: Record<string, string> = {
    'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
    'X-CMSP-Conversation-Id': args.conversationId,
  };
  if (args.authBearer) headers['Authorization'] = 'Bearer ' + args.authBearer;
  if (args.homeAccountId) {
    headers['X-CMSP-User-HomeAccountId'] = args.homeAccountId;
    if (args.username) headers['X-CMSP-User-Username'] = args.username;
  }

  const query = args.contentType ? `?contentType=${encodeURIComponent(args.contentType)}` : '';
  const res = await fetch(
    `/api/v1/chat/${encodeURIComponent(args.conversationId)}/attachments/${encodeURIComponent(args.fileId)}${query}`,
    { method: 'GET', headers: withConversationTrace(headers, args.conversationId) },
  );

  if (res.status === 410) {
    let reason: string | null = null;
    try {
      reason = ((await res.json()) as { reason?: string | null }).reason ?? null;
    } catch {
      reason = null;
    }
    throw new AttachmentUnavailableError(reason);
  }
  if (!res.ok) {
    throw new Error(`downloadLiveAttachment ${res.status}`);
  }

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const fileName = decodeURIComponent(match?.[1] ?? match?.[2] ?? 'attachment');
  return { blob: await res.blob(), fileName };
}

export class ChatStreamError extends Error {
  constructor(readonly code: string | undefined, message: string, readonly recoveryState?: StreamEvent['recoveryState'], readonly clientOperationId?: string) {
    super(message);
    this.name = 'ChatStreamError';
  }
}

export interface StreamCallbacks {
  onStart?: (event: StreamEvent) => void;
  onToken?: (text: string, message?: { messageKey?: string | null; messageArgs?: readonly string[] | null; caseDetails?: CmspCaseDetails | null }) => void;
  /**
   * Called for each activity-narration step ("Investigating your Teams problem"). Ephemeral: these are
   * never part of the reply and are not replayed when the conversation is resumed.
   */
  onProgress?: (step: ProgressStep) => void;
  /** Called for a token emitted in live mode (mode === "live"), carrying senderKind/liveCategory. */
  onLiveToken?: (event: StreamEvent) => void;
  /**
   * Ephemeral progress waypoint emitted while a turn is being processed (e.g. "Getting a specialist…"
   * or a Cortex activity update). NOT part of the reply text — the widget shows it transiently
   * and clears it once reply tokens arrive or the turn is done.
   */
  onStatus?: (text: string) => void;
  onDone?: (event: StreamEvent) => void;
  /** A browser live channel exists despite a failed send; subscribe without claiming delivery. */
  onLiveReady?: (event: StreamEvent) => void;
  onError?: (error: Error) => void;
}

export function sendChatMessageStream(
  args: {
    message: string;
    clientOperationId?: string;
    conversationId?: string;
    authBearer?: string | null;
    homeAccountId?: string;
    username?: string;
    displayName?: string;
    /** When true, identity was cached via initChat — skip identity headers. */
    useInitChat?: boolean;
    /** Override the entry point (e.g. from the agent selector). Falls back to detectEntryPoint(). */
    entryPoint?: string;
    /** Pin a specific agent by name, bypassing server routing rules (e.g. from the agent selector). */
    agentName?: string;
    /** Override the Accept-Language locale (e.g. from a locale-prefixed URL like /en-us/rewards). */
    locale?: string;
    /** Live-agent poll turn: a message-less drain of the live thread (exempt from the turn rate limit). */
    poll?: boolean;
    /**
     * Answer to a ChatPrompt the server streamed earlier. Like `poll`, this is a message-less turn
     * that never reaches the model — it dispatches server-side to the handler that owns the verb.
     */
    promptResponse?: ChatPromptResponse;
  },
  callbacks: StreamCallbacks,
): AbortController {
  const controller = new AbortController();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-CMSP-Anonymous-Session-Id': getAnonSessionId(),
    'X-CMSP-Entry-Point': args.entryPoint ?? detectEntryPoint(),
    'Accept-Language': args.locale || navigator.language || 'en-US',
  };

  if (args.agentName) {
    headers['X-CMSP-Agent-Name'] = args.agentName;
  }

  if (args.authBearer) {
    headers['Authorization'] = `Bearer ${args.authBearer}`;
  }

  if (args.conversationId) {
    headers['X-CMSP-Conversation-Id'] = args.conversationId;
  }

  // Only send identity headers when NOT using initChat (legacy fallback).
  if (!args.useInitChat && args.homeAccountId) {
    headers['X-CMSP-User-HomeAccountId'] = args.homeAccountId;
    if (args.username) headers['X-CMSP-User-Username'] = args.username;
    if (args.displayName) headers['X-CMSP-User-DisplayName'] = args.displayName;
  }

  let handledUnsentReady = false;
  const beganInBrowserLive = !!args.conversationId && browserLive.isBound(args.conversationId);
  let assistantReply = '';
  let assistantCaseDetails: CmspCaseDetails | undefined;
  let rememberedAiTurn = false;
  const pendingLiveTokens: StreamEvent[] = [];
  const dispatchEvent = async (eventName: string, data: string, replay = false): Promise<boolean> => {
    if (!data) return false;
    let parsed: Partial<StreamEvent> & { code?: string; message?: string };
    try {
      parsed = JSON.parse(data) as Partial<StreamEvent> & { code?: string; message?: string };
    } catch {
      return false;
    }
    const rawEvent = { ...parsed, type: parsed.type ?? eventName } as StreamEvent;
    // A bridge can emit its first live messages before the handoff's final done.
    // Do not expose those as a browser handoff while init capability is provisional.
    if (!replay && rawEvent.type === 'token' && rawEvent.mode === 'live'
      && args.conversationId && browserLive.enabled(args.conversationId) && !browserLive.isBound(args.conversationId)) {
      pendingLiveTokens.push(rawEvent);
      return false;
    }
    const event = args.conversationId ? browserLive.observe(args.conversationId, rawEvent) : rawEvent;
    if (args.conversationId && browserLive.isBound(args.conversationId)) event.conversationId ??= args.conversationId;
    if (event.type === 'done') {
      for (const token of pendingLiveTokens.splice(0)) {
        await dispatchEvent(token.type, JSON.stringify(token), true);
      }
    }
    switch (event.type) {
      case 'start':
        callbacks.onStart?.(event);
        return false;
      case 'token':
        if (event.mode === 'live' && callbacks.onLiveToken) {
          callbacks.onLiveToken(event);
        } else {
          assistantReply += event.text ?? '';
          if (event.caseDetails) assistantCaseDetails = event.caseDetails;
          callbacks.onToken?.(event.text ?? '', {
            messageKey: event.messageKey,
            messageArgs: event.messageArgs,
            ...(event.caseDetails ? { caseDetails: event.caseDetails } : {}),
          });
        }
        return false;
      case 'progress':
        // Text-less progress frames are dropped rather than rendered as an empty step: the server only
        // ever sends authored wording, so a blank one means something upstream lost the label.
        if (event.text) {
          callbacks.onProgress?.({
            phase: event.progressPhase ?? null,
            text: event.text,
            sequence: event.progressSequence ?? 0,
          });
        }
        return false;
      // Sibling of 'progress', deliberately kept separate (ADR-0009): a status is a transient routing
      // waypoint that REPLACES its predecessor, where a progress step is authored, sequenced, and
      // accumulates. Same-looking payload, different lifetime — do not collapse the two arms.
      case 'status':
        callbacks.onStatus?.(event.text ?? '');
        return false;
      case 'done':
        if (args.conversationId && !beganInBrowserLive && !args.poll && (!args.promptResponse || assistantCaseDetails)
          && event.retryMessage !== true && !rememberedAiTurn) {
          rememberedAiTurn = true;
          browserLive.rememberAiTurn(args.conversationId, args.promptResponse ? undefined : args.message,
            assistantReply || event.text || event.reply, assistantCaseDetails);
        }
        if (args.conversationId && browserLive.isBound(args.conversationId) && event.retryMessage === true) {
          if (handledUnsentReady) return true;
          handledUnsentReady = true;
          delete event.retryMessage;
          if (!args.poll && !args.promptResponse && args.message.trim()) {
            try {
              const confirmation: unknown = await browserLive.forwardUnsent(args.conversationId, args.message, controller.signal, args.clientOperationId);
              if (confirmation && typeof confirmation === 'object') {
                const receipt = confirmation as Partial<StreamEvent>;
                if (receipt.clientOperationId && typeof receipt.messageId === 'string' && receipt.messageId.trim()) {
                  event.clientOperationId = receipt.clientOperationId;
                  event.messageId = receipt.messageId;
                }
                if (typeof receipt.messageId === 'string' && receipt.messageId.trim()
                  && Number.isSafeInteger(receipt.readSequence) && (receipt.readSequence ?? 0) > 0) {
                  event.messageId = receipt.messageId;
                  event.readSequence = receipt.readSequence;
                  event.readOn = receipt.readOn;
                }
              }
            } catch (error) {
              if (!controller.signal.aborted) {
                callbacks.onLiveReady?.(event);
                callbacks.onError?.(error as Error);
              }
              return true;
            }
          }
        }
        if (controller.signal.aborted) return true;
        if (event.clientOperationId && typeof event.messageId === 'string' && event.messageId.trim()) {
          callbacks.onLiveToken?.({ ...event, type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'message-sent' });
        }
        callbacks.onDone?.(event);
        return true;
      case 'error':
        if (args.poll && ['send_status_unknown', 'send_confirmation_pending'].includes(event.error ?? '')) {
          if (event.clientOperationId) callbacks.onLiveToken?.({ ...event, type: 'token', mode: 'live', senderKind: 'system' });
          return false;
        }
        callbacks.onError?.(new ChatStreamError(event.error ?? parsed.code, event.error === 'live_agent_unavailable'
          ? event.text || 'Live agent is not available right now.'
          : event.recoveryState ? event.text || 'Live support is unavailable. Please end this chat.'
          : event.error ?? parsed.message ?? parsed.code ?? 'Unknown SSE error', event.recoveryState, event.clientOperationId));
        return true;
      default:
        return false;
    }
  };

  const processFrame = (frame: string) => {
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const rawLine of frame.split('\n')) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }

    return dispatchEvent(eventName, dataLines.join('\n'));
  };

  (async () => {
    let sawDone = false;
    try {
      if (args.conversationId) {
        liveHeaders.set(args.conversationId, headers);
        if (browserLive.isBound(args.conversationId)) {
          if (args.poll) {
            for await (const event of browserLive.events(args.conversationId, controller.signal)) {
              if (controller.signal.aborted) return;
              await dispatchEvent(event.type, JSON.stringify(event));
              // The existing surface treats onError as a completed receive turn
              // and retries later. Release this subscriber before that retry.
              if (event.type === 'error' && !['send_status_unknown', 'send_confirmation_pending'].includes(event.error ?? '')) return;
            }
          } else {
            if (args.promptResponse) throw new Error('A live conversation cannot submit an AI prompt.');
            const event = await browserLive.send(args.conversationId, args.message, controller.signal, args.clientOperationId);
            if (!controller.signal.aborted) await dispatchEvent(event.type, JSON.stringify(event));
          }
          return;
        }
      }
      const res = await fetch('/api/v1/chat/stream', {
        method: 'POST',
        headers: withConversationTrace(headers, args.conversationId),
        body: JSON.stringify({
          message: args.message,
          conversationId: args.conversationId,
          ...(args.promptResponse ? { promptResponse: args.promptResponse } : {}),
          ...(args.poll || args.promptResponse
            ? { ...(args.poll ? { poll: true } : {}) }
            : { clientContext: await collectClientContext(args.conversationId ?? getAnonSessionId()) }),
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`chat stream ${res.status}: ${await res.text()}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('ReadableStream not available');

      const decoder = new TextDecoder();
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          buffer += decoder.decode();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          sawDone = (await processFrame(frame)) || sawDone;
        }
      }

      if (buffer.trim()) {
        sawDone = (await processFrame(buffer)) || sawDone;
      }

      if (!sawDone && !controller.signal.aborted) {
        callbacks.onError?.(new Error('Response interrupted before completion.'));
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        callbacks.onError?.(err as Error);
      }
    }
  })();

  return controller;
}
